import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Popconfirm,
  Row,
  Col,
  Table,
  Tag,
  Tooltip,
  Image,
} from "antd";
import { GuestProfile, FrontDeskBooking } from "../../../types";
import { frontDeskService } from "../../../services/adminService";
import { hasPermission } from "../../../utils/permissions";
import { RadioCardGroup } from "./RadioCardGroup";
import dayjs from "dayjs";

interface ViewGuestProfileModalProps {
  open: boolean;
  guestProfile: GuestProfile | null;
  onCancel: () => void;
  onRefresh: () => void;
  onViewBooking: (booking: FrontDeskBooking) => void;
}

export const ViewGuestProfileModal: React.FC<ViewGuestProfileModalProps> = ({
  open,
  guestProfile,
  onCancel,
  onRefresh,
  onViewBooking,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [fullProfile, setFullProfile] = useState<any>(null);

  const idDocumentType = Form.useWatch("id_document_type", form);

  const getIdDocumentLabel = () => {
    switch (idDocumentType) {
      case "passport":
        return "Passport Number";
      case "national_id":
        return "National ID Number";
      case "driving_license":
        return "Driving License Number";
      default:
        return "ID Document Number";
    }
  };

  // Backend requires guests:edit for PUT/DELETE on /operations/crm/guests/:id.
  const canEditProfile = hasPermission("guests:edit");

  const canDelete =
    canEditProfile &&
    (!fullProfile?.stay_history ||
    fullProfile.stay_history.length === 0 ||
    fullProfile.stay_history.every((b: any) => {
      if (b.is_group_parent && b.groupChildren) {
        return b.groupChildren.every((c: any) => c.status === "checked_out");
      }
      return b.status === "checked_out";
    }));

  useEffect(() => {
    if (open && guestProfile) {
      form.setFieldsValue({
        full_name: guestProfile.full_name,
        email: guestProfile.email,
        phone: guestProfile.phone,
        id_document_type: guestProfile.id_document_type,
        id_document_number: guestProfile.id_document_number,
        id_document_url: guestProfile.id_document_url,
        address_line_1: guestProfile.address_line_1,
        address_line_2: guestProfile.address_line_2,
        preferences: guestProfile.preferences,
        notes: guestProfile.notes,
      });
      fetchFullProfile(guestProfile.id);
    } else {
      form.resetFields();
      setFullProfile(null);
    }
  }, [open, guestProfile, form]);

  const fetchFullProfile = async (id: string) => {
    try {
      setFetchingProfile(true);
      const res = await frontDeskService.getGuestProfileById(id);

      if (res && res.stay_history) {
        const parents = new Map<string, any>();
        res.stay_history.forEach((b: any) => {
          if (!b.group_id || b.group_id === b.id) {
            if (!parents.has(b.id)) {
              parents.set(b.id, { ...b, is_group_parent: false });
            }
          } else {
            if (!parents.has(b.group_id)) {
              parents.set(b.group_id, {
                ...b,
                id: b.group_id,
                is_group_parent: true,
                groupChildren: [],
                total_price: 0,
                room_number: 'Multiple',
                room_type_name: 'Multiple',
              });
            }
            const parent = parents.get(b.group_id);
            parent.total_price += Number(b.total_price || 0);
            parent.groupChildren.push({ ...b, is_group_child: true });
          }
        });
        res.stay_history = Array.from(parents.values());
      }

      setFullProfile(res);
    } catch (error) {
      console.error(error);
      message.error("Failed to load full guest profile details");
    } finally {
      setFetchingProfile(false);
    }
  };

  const handleUpdate = async (values: any) => {
    if (!guestProfile) return;
    try {
      setLoading(true);
      await frontDeskService.updateGuestProfile(guestProfile.id, values);
      message.success("Guest profile updated successfully");
      onRefresh();
      onCancel();
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message || "Failed to update guest profile",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!guestProfile) return;
    try {
      setDeleteLoading(true);
      await frontDeskService.deleteGuestProfile(guestProfile.id);
      message.success("Guest profile deleted successfully");
      onRefresh();
      onCancel();
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message || "Failed to delete guest profile",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const bookingColumns = [
    {
      title: "Confirm #",
      dataIndex: "confirmation_number",
      key: "confirmation_number",
      render: (text: string, record: any) => (
        <span>
          {text}
          {record.is_group_parent && (
            <Tag color="geekblue" style={{ marginLeft: 8 }}>
              BULK
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Check-in",
      dataIndex: "check_in_date",
      key: "check_in_date",
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Check-out",
      dataIndex: "check_out_date",
      key: "check_out_date",
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      render: (price: number) =>
        `LKR ${Number(price || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        let color = "blue";
        if (status === "confirmed") color = "green";
        if (status === "checked_in") color = "purple";
        if (status === "checked_out") color = "default";
        if (status === "cancelled") color = "red";
        if (status === "reserved") color = "geekblue";
        if (status === "blocked") color = "volcano";
        if (status === "no_show") color = "magenta";
        return (
          <Tag color={color}>{status.replace("_", " ").toUpperCase()}</Tag>
        );
      },
    },
  ];

  return (
    <Modal
      title="Guest Profile Details"
      open={open}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Tooltip
          key="delete-tooltip"
          title={
            !canEditProfile
              ? "You don't have permission to edit guest profiles."
              : !canDelete
              ? "Cannot delete guest with active or pending bookings."
              : ""
          }
        >
          <span style={{ display: "inline-block" }}>
            <Popconfirm
              key="delete"
              title="Delete Guest Profile"
              description="Are you sure you want to delete this guest profile?"
              onConfirm={handleDelete}
              okText="Yes, Delete"
              cancelText="No"
              disabled={!canDelete}
            >
              <Button danger loading={deleteLoading} disabled={!canDelete}>
                Delete Profile
              </Button>
            </Popconfirm>
          </span>
        </Tooltip>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          disabled={!canEditProfile}
          onClick={() => form.submit()}
        >
          Update Profile
        </Button>,
      ]}
    >
      <Row gutter={24}>
        <Col span={8}>
          <h3 style={{ marginTop: 0 }}>Personal Details</h3>
          <Form form={form} layout="vertical" onFinish={handleUpdate}>
            <Form.Item
              name="full_name"
              label="Full Name"
              rules={[
                { required: true, message: "Please enter the guest's name" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email Address">
              <Input type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Phone Number">
              <Input />
            </Form.Item>
            <Form.Item name="id_document_type" label="ID Document Type">
              <RadioCardGroup
                options={[
                  { label: "Passport", value: "passport" },
                  { label: "National ID", value: "national_id" },
                  { label: "Driving License", value: "driving_license" },
                ]}
              />
            </Form.Item>
            {idDocumentType && (
              <Form.Item name="id_document_number" label={getIdDocumentLabel()}>
                <Input />
              </Form.Item>
            )}
            <Form.Item label="ID Document Image">
              {form.getFieldValue("id_document_url") ? (
                <Image
                  src={form.getFieldValue("id_document_url")}
                  alt="ID Document"
                  style={{ maxHeight: 150, borderRadius: 4 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F/PQAJhAN4qy22kQAAAABJRU5ErkJggg=="
                />
              ) : (
                <span style={{ color: "#999" }}>No document uploaded</span>
              )}
            </Form.Item>
            <Form.Item name="address_line_1" label="Address Line 1">
              <Input />
            </Form.Item>
            <Form.Item name="address_line_2" label="Address Line 2 (optional)">
              <Input />
            </Form.Item>
            <Form.Item name="preferences" label="Preferences">
              <Input.TextArea
                rows={2}
                placeholder="Any specific room preferences, bed type, etc."
              />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea
                rows={2}
                placeholder="Staff notes about the guest"
              />
            </Form.Item>
          </Form>
        </Col>
        <Col span={16}>
          <h3 style={{ marginTop: 0 }}>Booking History</h3>
          <Table
            size="small"
            loading={fetchingProfile}
            dataSource={fullProfile?.stay_history || []}
            columns={bookingColumns}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            expandable={{ expandIconColumnIndex: -1 }}
            onRow={(record: any) => ({
              onClick: () => {
                onViewBooking(record as FrontDeskBooking);
              },
              style: { cursor: "pointer" },
            })}
            locale={{ emptyText: "No booking history found." }}
          />
        </Col>
      </Row>
    </Modal>
  );
};
